import React, {useEffect, useState} from "react";
import {Alert, Form, Table} from "react-bootstrap";
import axios from "axios";
import {API_URL} from "../../../../config";
import {checklistStateLabel, monthDate, monthKey, monthYear, tileMonth, viewableMonths} from "./rsoForecastWindowHelpers";
import RsoSignInExpired, {isSignInExpired} from "./RsoSignInExpired";

const count = (value) => (Number.isSafeInteger(value) && value >= 0 ? value.toLocaleString() : "—");
const named = (value, fallback) => (typeof value === "string" && value.trim() ? value.trim() : fallback);

// Read-only "who still has properties to answer" list for sales managers.
export default function RsoTeamInputStatus({token, entryWindow, active}) {
  const months = viewableMonths(entryWindow);
  const [month, setMonth] = useState(() => tileMonth(entryWindow) || months[0]);
  const [result, setResult] = useState({status: "loading", data: null, message: ""});
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!active || !token || !monthKey(month)) return undefined;
    let current = true;
    setResult({status: "loading", data: null, message: ""});
    axios.get(`${API_URL}/Dashboard/api/RsoInputStatus`, {headers: {Authorization: `Bearer ${token}`},
      params: {forecastMonth: monthDate(month)}, timeout: 60000}).then(({data}) => {
      if (!current) return;
      if (!Array.isArray(data?.salespeople) || monthKey(data.forecastMonth) !== monthKey(month)) throw new Error("Invalid team status");
      setResult({status: "ready", data, message: ""});
    }).catch((failure) => {
      if (!current) return;
      const message = failure?.response?.data?.message;
      setResult({status: "error", data: null, signInExpired: isSignInExpired(failure), message: failure?.response?.status === 403
        ? "Your account can't view the team list."
        : typeof message === "string" && message ? message : "Could not load the team list. Try again."});
    });
    return () => {current = false;};
  }, [active, token, month, attempt]);
  const data = result.data;
  return <section className="rsf-team-status" aria-label="Team input status">
    <h4 tabIndex="-1">Team input status</h4>
    <p>Salespeople and the RSO properties they still need to answer for {monthYear(month)}. This list is view only.</p>
    {months.length > 1 && <Form.Group controlId="rsf-team-month" className="mb-3">
      <Form.Label>Month</Form.Label>
      <Form.Select value={month} onChange={(event) => setMonth(event.target.value)}>
        {months.map((value) => <option key={value} value={value}>{monthYear(value)}</option>)}
      </Form.Select>
    </Form.Group>}
    {result.status === "loading" && <p role="status">Loading the team list…</p>}
    {result.status === "error" && (result.signInExpired ? <RsoSignInExpired /> : <Alert variant="danger">{result.message}{" "}
      <button type="button" className="btn btn-link p-0 align-baseline" onClick={() => setAttempt((value) => value + 1)}>Try again</button></Alert>)}
    {data && <>
      {data.isClosed === true && <p><strong>{monthYear(month)} entry is closed.</strong></p>}
      {named(data.countsNote, "") && <p className="small">{data.countsNote}</p>}
      {!data.salespeople.length ? <p>No salespeople have RSO properties to answer for {monthYear(month)}.</p>
        : <Table responsive size="sm" className="rsf-team-table"><thead><tr>
          <th scope="col">Salesperson</th><th scope="col" className="rsf-number">Required</th><th scope="col" className="rsf-number">Done</th>
          <th scope="col" className="rsf-number">No units</th><th scope="col" className="rsf-number">Covered by change</th>
          <th scope="col" className="rsf-number">Left</th><th scope="col" className="rsf-number">Changes not sent</th><th scope="col">Still to answer</th>
        </tr></thead><tbody>{data.salespeople.map((person, index) => {
          const missing = Array.isArray(person.missingProperties) ? person.missingProperties : [];
          const unknown = Array.isArray(person.unknownProperties) ? person.unknownProperties : [];
          return <tr key={`${person.salespersonId ?? "person"}-${index}`}>
            <th scope="row">{named(person.name, `Salesperson #${person.salespersonId}`)}</th>
            <td className="rsf-number">{count(person.required)}</td><td className="rsf-number">{count(person.done)}</td>
            <td className="rsf-number">{count(person.noUnits)}</td><td className="rsf-number">{count(person.covered)}</td>
            <td className="rsf-number">{count(person.left)}</td><td className="rsf-number">{count(person.unsentChanges)}</td>
            <td>{!missing.length && !unknown.length ? "None" : <ul className="mb-0 ps-3">
              {missing.map((property, row) => <li key={`m-${property.contactInfoId ?? row}`}>{named(property.name, `Property #${property.contactInfoId}`)}
                {checklistStateLabel(property) && <small className="d-block text-muted">{checklistStateLabel(property)}</small>}</li>)}
              {unknown.map((property, row) => <li key={`u-${property.contactInfoId ?? row}`}>{named(property.name, `Property #${property.contactInfoId}`)}
                <small className="d-block text-muted">RSO status unknown{named(property.reason, "") ? `: ${property.reason}` : ""}</small></li>)}
            </ul>}</td>
          </tr>;
        })}</tbody></Table>}
    </>}
  </section>;
}
